import { Divider, Grid, Typography } from '@material-ui/core'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import React, { useEffect, useState, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { compose } from 'recompose'
import getFiltersSelectedAccount from '../../State/Store/Filters/Selectors/FiltersSelectedAccount.selector'
import {
  UI_NOTIFICATION_PANEL_INFORMATION_MODAL_CLOSE,
  UI_NOTIFICATION_PANEL_INFORMATION_MODAL_OPEN,
} from '../../State/Store/UI/UI.types'
import helpers from '../../Support/Text'
import NotificationConfigsComp from './NotificationConfigs/NotificationConfigs.component'
import NotificationsSettingsStyles from './styles'
import ExtraInfoModal from './parts/ExtraInfoModal.comp'
import AccountSelector from './parts/AccountSelector.comp'
import ProjectSelector from './parts/ProjectSelector.comp'
import { NotificationConfig } from 'types/NotificationConfig'
import { Project } from 'types/Project'
import { Account } from 'types/Account'
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer'
import { sortedNotificationConfigs } from './utils/sorted-configs'
import ServicesContext from 'Contexts/Services/Services.context'
import hitGTM from 'State/Store/UI/Actions/GTM.action'
import { WithGlobalStylesProps } from 'types/styles/WithGlobalStylesProps'
import { withGlobalStyles } from 'Support/Theme/CreateInjectableStyles/withGlobalStyles'
import { openSnackbar } from 'State/Store/Snackbar/SnackbarActionCreators'

interface Props {
  any?: any,
}
type FProps = Props & WithGlobalStylesProps<any>

const NotificationsSettingsComp = (props: FProps) => {
  const { classes } = props
  const [ configs, setConfigs ] = useState<NotificationConfig[]>([])
  const [ hasErrorSaving, setHasErrorSaving ] = useState<boolean>(false)
  const [ accounts, setAccounts ] = useState<Account[]>([])
  const [ accountSelected, setAccountSelected ] = useState<null | Account>(null)
  const [ projectSelected, setProjectSelected ] = useState<undefined | null | Project >(undefined)
  const [ showInformation, setShowInformation ] = useState<boolean>(false)
  const [ isSaving, setIsSaving ] = useState<boolean>(false)
  const dispatch = useDispatch()
  const services = useContext(ServicesContext)
  const currentAccountGlobal = useSelector(getFiltersSelectedAccount)

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const loadedAccounts = await services.orderService.getPlaceOrderAccounts()
        setAccounts(loadedAccounts)
        if (loadedAccounts.length !== 0) {
          const foundAccount = loadedAccounts.find((el: any) => helpers.getNumberFromBillTo(currentAccountGlobal.trim()) === el.id.trim())
          const upcomingAccount = currentAccountGlobal ? foundAccount : loadedAccounts[0]
          setAccountSelected(upcomingAccount)
        }
      } catch (ex) {
        dispatch(openSnackbar({ message: `Something went wrong while fetching accounts` }))
      }
    }
    // request list of accounts from api
    fetchAccounts()
  }, [])

  useEffect(() => {
    const getAccountConfig = async () => {
      try {
        if (accountSelected === null) return
        const upcomingConfigs = await services.notificationService.getAccountConfig(accountSelected.id.trim())
        setConfigs(sortedNotificationConfigs(upcomingConfigs))
      } catch (ex) {
        dispatch(openSnackbar({ message: `Something went wrong while fetching settings for this account` }))
      }
    }
    const getProjectConfig = async () => {
      try {
        if (accountSelected === null) return
        if (projectSelected === null || projectSelected === undefined) return
        const upcomingConfigs = await services.notificationService.getProjectConfig(projectSelected.id.trim())
        setConfigs(sortedNotificationConfigs(upcomingConfigs))
      } catch (ex) {
        dispatch(openSnackbar({ message: `Something went wrong while fetching settings for this project` }))
      }
    }
    const fetchAsync = async () => {
      // fetch account or project settings
      if (accountSelected && projectSelected === null) {
        getAccountConfig()
      } else if (accountSelected && projectSelected === undefined) {
        const result = await services.orderService.getDefaultProjectForAccount(accountSelected)
        if (result.projectCode) {
          if (accountSelected && accountSelected.projects) {
            const foundProject = accountSelected.projects.find((project: Project) => {
              return project.id.trim() === result.projectCode.trim()
            })
            if (foundProject) {
              setProjectSelected(foundProject)
            }
          }
        } else {
          getAccountConfig()
        }
      } else if (accountSelected && projectSelected) {
        getProjectConfig()
      }
    }
    fetchAsync()
  }, [accountSelected, projectSelected])

  const onShowInformation = () => {
    dispatch(hitGTM(UI_NOTIFICATION_PANEL_INFORMATION_MODAL_OPEN))
    setShowInformation(true)
  }

  const onHideInformation = () => {
    dispatch(hitGTM(UI_NOTIFICATION_PANEL_INFORMATION_MODAL_CLOSE))
    setShowInformation(false)
  }

  const onSaveHandler = async () => {
    if (accountSelected === null) return
    try {
      setIsSaving(true)
      await services.notificationService.saveConfigs({
        belongsToId: projectSelected ? projectSelected.id : accountSelected.id,
        accountId: accountSelected.id,
        configs: configs,
        isProjectConfig: !!projectSelected,
      })
      try {
        setConfigs([...configs])
        setHasErrorSaving(false);
        dispatch(openSnackbar({ message: `Your settings have been applied` }))
      } catch (ex) {
        dispatch(openSnackbar({ message: `Something went wrong while fetching settings for this project` }))
        setHasErrorSaving(true)
      }
    } catch (ex) {
      dispatch(openSnackbar({ message: `Something went wrong while saving settings for this ${projectSelected ? 'project' : 'account'}` }))
      setHasErrorSaving(true)
    } finally {
      setIsSaving(false)
    }
  }

  const onAccountChange = (account: Account) => {
    setAccountSelected(account)
    setProjectSelected(undefined)
  }
  const onProjectChange = (project: Project) => {
    setProjectSelected(project)
  }
  const onNoneProjectSeleted = () => {
    setProjectSelected(null)
  }

  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={12} className={classes.notificationsPanelContainer}>
          <Grid container spacing={2} className={classes.notificationsConfigsContainer}>
            <Grid item xs={12}>
              <Typography variant='body2'>
                { `Notifications can be configured to show within this App or via SMS` }
                <InfoOutlined className={classes.infoIcon} onClick={onShowInformation} />
              </Typography>
              <Divider className={classes.divider} />
            </Grid>
            <Grid item xs={12} data-mf-replace-inner={MFSDR}>
              <AccountSelector
                classes={classes}
                selected={accountSelected}
                options={accounts}
                onChange={onAccountChange}
                helperText={`Select an account to configure its notifications`}
              />
            </Grid>
            {
              (accountSelected && accountSelected.projects && accountSelected.projects.length && projectSelected !== undefined)
              && (
                <Grid item xs={12} data-mf-replace-inner={MFSDR}>
                  <ProjectSelector
                    classes={classes}
                    selected={projectSelected}
                    options={accountSelected ? accountSelected.projects : []}
                    withNoneOption={true}
                    onNoneSelected={onNoneProjectSeleted}
                    onChange={onProjectChange}
                    helperText={`Select a project to configure its notifications`}
                  />
                </Grid>
              )
            }
            {accountSelected && (
              <Grid item xs={12} key={projectSelected ? projectSelected.id : accountSelected.id}>
                <NotificationConfigsComp
                  isSaving={isSaving}
                  onChange={(configs: NotificationConfig[]) => setConfigs(configs)}
                  onSave={onSaveHandler}
                  belongsToId={projectSelected ? projectSelected.id : accountSelected.id}
                  configs={configs}
                  hasErrorSaving={hasErrorSaving}
                  />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <ExtraInfoModal
        classes={classes}
        open={showInformation}
        onClose={onHideInformation}
      />

    </React.Fragment>
  )
}

const NotificationsSettings = compose<FProps, Props>(
  withGlobalStyles(NotificationsSettingsStyles),
)(NotificationsSettingsComp)

export default NotificationsSettings
